import type { ChildTextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import { setReactiveWorkspaceActions } from 'api/graphQL/reactiveVars/workspaceActions'
import { TrackChangeType } from 'models/changes'
import { PROPERTY_EDITOR_EDITABLE_TEXT_VARIANT as NODE_TYPES } from 'pages/ProjectsPage/WorkspacePage/components/constants'
import { MutableRefObject, useCallback } from 'react'
import { v4 as newGuid } from 'uuid'
import { useWorkspace2Store } from '../../../Workspace2Store'
import type { ContentItemViewNodesType } from '../../../types/contentItemViewNode.type'
import { pillInlineTextBeforeUOM } from '../uomPillState'

// ─── Scenario 2: insert UOM into a newly-inserted pill ────────────────────────
// Used by useInsertPillAtCursor — builds and returns omWorkspaceInsertUOMHandler
// right after a new pill is created at the CKEditor cursor position.

interface UOMInsertEditorRefs {
    debouncedOnChange: { cancel: () => void }
    deactivateCKEditor: () => void
    updateTreeViewNodes: (nodes: Partial<ContentItemViewNodesType>[]) => void
    setUnsavedData: (data: string) => void
}

const makeTextEl = (text: string, authoredUserId: string) => ({
    id: newGuid(),
    type: NODE_TYPES.Text,
    text,
    elements: [],
    imperialUnits: '',
    metricUnits: '',
    changeType: TrackChangeType.ADDED,
    placeholderText: '',
    authoredUserId,
})

const makeUOMEl = (authoredUserId: string) => ({
    id: newGuid(),
    type: NODE_TYPES.UnitsOfMeasure,
    text: '',
    elements: [],
    imperialUnits: '',
    metricUnits: '',
    changeType: TrackChangeType.ADDED,
    placeholderText: '',
    authoredUserId,
})

const hasActiveAddedUOM = (
    nodes: Partial<ContentItemViewNodesType>[],
    soId: string
): boolean =>
    nodes.some((node) =>
        (node.elements ?? []).some(
            (og) =>
                og.type === NODE_TYPES.OptionGroup &&
                (og.elements ?? []).some(
                    (so) =>
                        so.id === soId &&
                        (so.elements ?? []).some(
                            (el) =>
                                el.type === NODE_TYPES.UnitsOfMeasure &&
                                el.changeType === TrackChangeType.ADDED
                        )
                )
        )
    )

// Converts a freshly-inserted blank pill into a Text→UOM→Text inline editor scaffold when invoked.
export const buildUOMHandlerForNewPill = (
    capturedNodes: Partial<ContentItemViewNodesType>[],
    capturedOgId: string | null,
    newSelectionOptionId: string,
    authoredUserId: string,
    refsRef: MutableRefObject<UOMInsertEditorRefs>,
    skipNextBlurRef: MutableRefObject<boolean>
): (() => void) => {
    return () => {
        const refs = refsRef.current
        // Always clean up inline text first — even if we bail early, stale text
        // from a prior drag-off-button abort should not persist into the next insert.
        const initialText = pillInlineTextBeforeUOM.get(newSelectionOptionId) ?? ''
        pillInlineTextBeforeUOM.delete(newSelectionOptionId)
        // Idempotency: if the target SO already has an ADDED UOM in the live store,
        // a prior handler fire already built the scaffold — skip to avoid overwriting it.
        const liveNodes = useWorkspace2Store.getState().contentItemViewNodes
        if (hasActiveAddedUOM(liveNodes, newSelectionOptionId)) return

        const updatedUOMNodes = capturedNodes.map((node) => {
            const elements = node.elements ?? []
            const ogIndex = elements.findIndex((el) => {
                if (el.type !== NODE_TYPES.OptionGroup) return false
                if (capturedOgId && el.id === capturedOgId) return true
                if (el.elements?.some((c) => c.id === newSelectionOptionId)) return true
                return false
            })
            if (ogIndex === -1) return node
            const og = elements[ogIndex]
            const selectionOptions = (og.elements ?? []).filter(
                (c) => c.type === NODE_TYPES.SelectionOption
            )
            const targetSO =
                selectionOptions.find((c) => c.id === newSelectionOptionId) ??
                selectionOptions[0]
            if (!targetSO) return node
            const updatedSO = {
                ...targetSO,
                elements: [makeTextEl(initialText, authoredUserId), makeUOMEl(authoredUserId), makeTextEl('', authoredUserId)],
            }
            const newOgElements = (og.elements ?? []).map((c) =>
                c === targetSO ? updatedSO : c
            )
            return {
                ...node,
                elements: [
                    ...elements.slice(0, ogIndex),
                    { ...og, elements: newOgElements },
                    ...elements.slice(ogIndex + 1),
                ],
            }
        }) as Partial<ContentItemViewNodesType>[]

        refs.debouncedOnChange.cancel()
        skipNextBlurRef.current = true
        refs.deactivateCKEditor()
        refs.updateTreeViewNodes(updatedUOMNodes)
        refs.setUnsavedData('')
        setReactiveWorkspaceActions({
            isInsertPill: false,
            isInsertText: false,
            isInsertUOM: false,
            isAddSpecifierNote: false,
            omWorkspaceInsertUOMHandler: undefined,
        })
    }
}

// ─── Scenario 3: wrap a SelectionOption in two ReplacementOptions ─────────────
// Used by useEditorActionBarHandler — replaces an existing SelectionOption with
// two ReplacementOption nodes when Insert UOM is triggered on a plain pill.

export const useInsertUOM = () => {
    const insertUOMInPill = useCallback(
        (
            newPillId: string | null | undefined,
            optionGroupId: string | null | undefined,
            capturedNodes: Partial<ContentItemViewNodesType>[] | null | undefined
        ) => {
            const { contentItemViewNodes: storeNodes, actions } = useWorkspace2Store.getState()
            // Prefer the nodes captured right after pill insertion — store may have been reset since
            const currentNodes = capturedNodes ?? storeNodes

            const replacement1Id = newGuid()
            const replacement2Id = newGuid()

            const updatedNodes = currentNodes.map((node) => {
                const elements = node.elements ?? []

                const ogIndex = elements.findIndex((el) => {
                    if (el.type !== 'OptionGroup') return false
                    // Prefer matching by the OptionGroup's own ID
                    if (optionGroupId && el.id === optionGroupId) return true
                    // Fall back to matching by child SelectionOption ID
                    if (newPillId && el.elements?.some((c) => c.id === newPillId)) return true
                    return false
                })

                if (ogIndex === -1) return node

                const og = elements[ogIndex]
                const selectionOptions = (og.elements ?? []).filter(
                    (c) => c.type === NODE_TYPES.SelectionOption
                )
                const targetSO =
                    selectionOptions.find((c) => c.id === newPillId) ??
                    selectionOptions.find((c) => c.changeType === TrackChangeType.ADDED) ??
                    selectionOptions[0]

                if (!targetSO) return node

                const replacementOption1: Partial<ChildTextContentElement> = {
                    id: replacement1Id,
                    type: NODE_TYPES.ReplacementOption,
                    text: '',
                    placeholderText: '',
                    changeType: TrackChangeType.ADDED as 'ADDED',
                    optionItemState: null,
                    imperialUnits: '',
                    metricUnits: '',
                    authoredUserId: '',
                }
                const replacementOption2: Partial<ChildTextContentElement> = {
                    id: replacement2Id,
                    type: NODE_TYPES.ReplacementOption,
                    text: '',
                    placeholderText: '',
                    changeType: TrackChangeType.ADDED as 'ADDED',
                    optionItemState: null,
                    imperialUnits: '',
                    metricUnits: '',
                    authoredUserId: '',
                }

                const newOgElements = (og.elements ?? []).flatMap((c) =>
                    c === targetSO ? [replacementOption1, replacementOption2] : [c]
                )

                return {
                    ...node,
                    elements: [
                        ...elements.slice(0, ogIndex),
                        { ...og, elements: newOgElements },
                        ...elements.slice(ogIndex + 1),
                    ],
                }
            }) as Partial<ContentItemViewNodesType>[]

            actions.setContentItemViewNodes(updatedNodes)
            actions.setTextContentElementToAdd({ id: replacement1Id })
            setReactiveWorkspaceActions({
                isInsertPill: false,
                isInsertText: false,
                isInsertUOM: false,
                isAddSpecifierNote: false,
            })
        },
        []
    )

    return { insertUOMInPill }
}
